import { ApiError } from "@/lib/apiClient";
import {
  Customer,
  CustomerAddress,
  CustomerNote,
  CreateCustomerDto,
  UpdateCustomerDto,
  CreateCustomerAddressDto,
  CreateCustomerNoteDto,
  FetchCustomersOptions,
} from "@/types/models/customer";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SearchCustomersOptions } from "@/services/customerService";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface CustomersState {
  // Customer list state
  items: Customer[];
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  // Single customer state
  currentCustomer: Customer | null;
  customerLoading: boolean;
  customerError: ApiError | null;

  // Search state
  searchResults: Customer[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  // Customer addresses state
  addresses: CustomerAddress[];
  addressesLoading: boolean;
  addressesError: ApiError | null;

  // Customer notes state
  notes: CustomerNote[];
  notesLoading: boolean;
  notesError: ApiError | null;

  // Form states
  formState: {
    loading: boolean;
    success: boolean;
    error: ApiError | null;
  };

  addressFormState: {
    loading: boolean;
    success: boolean;
    error: ApiError | null;
  };

  noteFormState: {
    loading: boolean;
    success: boolean;
    error: ApiError | null;
  };

  // Dashboard state
  dashboard: {
    totalCustomers: number;
    newCustomers: number;
    repeatCustomers: number;
    avgOrderValue: number;
    loading: boolean;
    error: ApiError | null;
  };

  // Import/Export state
  importLoading: boolean;
  importError: ApiError | null;
  exportLoading: boolean;
  exportError: ApiError | null;
}

const initialState: CustomersState = {
  items: [],
  loading: false,
  error: null,
  pagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  currentCustomer: null,
  customerLoading: false,
  customerError: null,
  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  addresses: [],
  addressesLoading: false,
  addressesError: null,
  notes: [],
  notesLoading: false,
  notesError: null,
  formState: {
    loading: false,
    success: false,
    error: null,
  },
  addressFormState: {
    loading: false,
    success: false,
    error: null,
  },
  noteFormState: {
    loading: false,
    success: false,
    error: null,
  },
  dashboard: {
    totalCustomers: 0,
    newCustomers: 0,
    repeatCustomers: 0,
    avgOrderValue: 0,
    loading: false,
    error: null,
  },
  importLoading: false,
  importError: null,
  exportLoading: false,
  exportError: null,
};

const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    // Fetch Customers
    fetchCustomersRequest: (
      state,
      _action: PayloadAction<FetchCustomersOptions>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchCustomersSuccess: (
      state,
      action: PayloadAction<{
        list: Customer[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.items = action.payload.list;
      state.pagination.page = action.payload.page;
      state.pagination.total = action.payload.total;
      state.pagination.pageSize = action.payload.pageSize;
      state.pagination.totalPages = action.payload.totalPages;
      state.loading = false;
    },
    fetchCustomersFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Search Customers
    searchCustomersRequest: (
      state,
      _action: PayloadAction<SearchCustomersOptions>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchCustomersSuccess: (
      state,
      action: PayloadAction<{
        list: Customer[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.searchResults = action.payload.list;
      state.searchPagination.page = action.payload.page;
      state.searchPagination.total = action.payload.total;
      state.searchPagination.pageSize = action.payload.pageSize;
      state.searchPagination.totalPages = action.payload.totalPages;
      state.searchLoading = false;
    },
    searchCustomersFailure: (state, action: PayloadAction<ApiError>) => {
      state.searchError = action.payload;
      state.searchLoading = false;
    },

    // Single Customer Operations
    fetchCustomerRequest: (
      state,
      _action: PayloadAction<{ customerId: string }>,
    ) => {
      state.customerLoading = true;
      state.customerError = null;
    },
    fetchCustomerSuccess: (
      state,
      action: PayloadAction<{ customer: Customer }>,
    ) => {
      state.currentCustomer = action.payload.customer;
      state.customerLoading = false;
      state.customerError = null;
    },
    fetchCustomerFailure: (state, action: PayloadAction<ApiError>) => {
      state.customerLoading = false;
      state.customerError = action.payload;
    },

    // Create Customer
    createCustomerRequest: (
      state,
      _action: PayloadAction<CreateCustomerDto>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createCustomerSuccess: (
      state,
      action: PayloadAction<{ customer: Customer }>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items.unshift(action.payload.customer);
      state.pagination.total += 1;
    },
    createCustomerFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Update Customer
    updateCustomerRequest: (
      state,
      _action: PayloadAction<{
        customerId: string;
        customer: UpdateCustomerDto;
      }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateCustomerSuccess: (
      state,
      action: PayloadAction<{ customer: Customer }>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      const index = state.items.findIndex(
        (item) => item.id === action.payload.customer.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload.customer;
      }
      if (state.currentCustomer?.id === action.payload.customer.id) {
        state.currentCustomer = action.payload.customer;
      }
    },
    updateCustomerFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Delete Customer
    deleteCustomerRequest: (
      state,
      _action: PayloadAction<{ customerId: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    deleteCustomerSuccess: (
      state,
      action: PayloadAction<{ customerId: string }>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = state.items.filter(
        (item) => item.id !== action.payload.customerId,
      );
      state.pagination.total = Math.max(0, state.pagination.total - 1);
      if (state.currentCustomer?.id === action.payload.customerId) {
        state.currentCustomer = null;
      }
    },
    deleteCustomerFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    // Customer Addresses
    fetchCustomerAddressesRequest: (
      state,
      _action: PayloadAction<{
        customerId: string;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      state.addressesLoading = true;
      state.addressesError = null;
    },
    fetchCustomerAddressesSuccess: (
      state,
      action: PayloadAction<{ addresses: CustomerAddress[] }>,
    ) => {
      state.addresses = action.payload.addresses;
      state.addressesLoading = false;
    },
    fetchCustomerAddressesFailure: (state, action: PayloadAction<ApiError>) => {
      state.addressesLoading = false;
      state.addressesError = action.payload;
    },

    createCustomerAddressRequest: (
      state,
      _action: PayloadAction<{
        customerId: string;
        address: CreateCustomerAddressDto;
      }>,
    ) => {
      state.addressFormState.loading = true;
      state.addressFormState.error = null;
      state.addressFormState.success = false;
    },
    createCustomerAddressSuccess: (
      state,
      action: PayloadAction<{ address: CustomerAddress }>,
    ) => {
      state.addressFormState.loading = false;
      state.addressFormState.success = true;
      state.addresses.push(action.payload.address);
    },
    createCustomerAddressFailure: (state, action: PayloadAction<ApiError>) => {
      state.addressFormState.loading = false;
      state.addressFormState.error = action.payload;
      state.addressFormState.success = false;
    },

    deleteCustomerAddressRequest: (
      state,
      _action: PayloadAction<{ customerId: string; addressId: string }>,
    ) => {
      state.addressFormState.loading = true;
      state.addressFormState.error = null;
    },
    deleteCustomerAddressSuccess: (
      state,
      action: PayloadAction<{ addressId: string }>,
    ) => {
      state.addressFormState.loading = false;
      state.addressFormState.success = true;
      state.addresses = state.addresses.filter(
        (addr) => addr.id !== action.payload.addressId,
      );
    },
    deleteCustomerAddressFailure: (state, action: PayloadAction<ApiError>) => {
      state.addressFormState.loading = false;
      state.addressFormState.error = action.payload;
    },

    // Customer Notes
    fetchCustomerNotesRequest: (
      state,
      _action: PayloadAction<{
        customerId: string;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      state.notesLoading = true;
      state.notesError = null;
    },
    fetchCustomerNotesSuccess: (
      state,
      action: PayloadAction<{ notes: CustomerNote[] }>,
    ) => {
      state.notes = action.payload.notes;
      state.notesLoading = false;
    },
    fetchCustomerNotesFailure: (state, action: PayloadAction<ApiError>) => {
      state.notesLoading = false;
      state.notesError = action.payload;
    },

    createCustomerNoteRequest: (
      state,
      _action: PayloadAction<{
        customerId: string;
        note: CreateCustomerNoteDto;
      }>,
    ) => {
      state.noteFormState.loading = true;
      state.noteFormState.error = null;
      state.noteFormState.success = false;
    },
    createCustomerNoteSuccess: (
      state,
      action: PayloadAction<{ note: CustomerNote }>,
    ) => {
      state.noteFormState.loading = false;
      state.noteFormState.success = true;
      state.notes.unshift(action.payload.note);
    },
    createCustomerNoteFailure: (state, action: PayloadAction<ApiError>) => {
      state.noteFormState.loading = false;
      state.noteFormState.error = action.payload;
      state.noteFormState.success = false;
    },

    deleteCustomerNoteRequest: (
      state,
      _action: PayloadAction<{ customerId: string; noteId: string }>,
    ) => {
      state.noteFormState.loading = true;
      state.noteFormState.error = null;
    },
    deleteCustomerNoteSuccess: (
      state,
      action: PayloadAction<{ noteId: string }>,
    ) => {
      state.noteFormState.loading = false;
      state.noteFormState.success = true;
      state.notes = state.notes.filter(
        (note) => note.id !== action.payload.noteId,
      );
    },
    deleteCustomerNoteFailure: (state, action: PayloadAction<ApiError>) => {
      state.noteFormState.loading = false;
      state.noteFormState.error = action.payload;
    },

    // Dashboard
    fetchCustomerDashboardRequest: (state) => {
      state.dashboard.loading = true;
      state.dashboard.error = null;
    },
    fetchCustomerDashboardSuccess: (
      state,
      action: PayloadAction<{
        totalCustomers: number;
        newCustomers: number;
        repeatCustomers: number;
        avgOrderValue: number;
      }>,
    ) => {
      state.dashboard = {
        ...action.payload,
        loading: false,
        error: null,
      };
    },
    fetchCustomerDashboardFailure: (state, action: PayloadAction<ApiError>) => {
      state.dashboard.loading = false;
      state.dashboard.error = action.payload;
    },

    // Import/Export
    importCustomersRequest: (state, _action: PayloadAction<{ file: File }>) => {
      state.importLoading = true;
      state.importError = null;
    },
    importCustomersSuccess: (
      state,
      _action: PayloadAction<{ imported: number; updated: number }>,
    ) => {
      state.importLoading = false;
      state.formState.success = true;
    },
    importCustomersFailure: (state, action: PayloadAction<ApiError>) => {
      state.importLoading = false;
      state.importError = action.payload;
    },

    exportCustomersRequest: (state) => {
      state.exportLoading = true;
      state.exportError = null;
    },
    exportCustomersSuccess: (state) => {
      state.exportLoading = false;
    },
    exportCustomersFailure: (state, action: PayloadAction<ApiError>) => {
      state.exportLoading = false;
      state.exportError = action.payload;
    },

    // Reset states
    resetFormState: (state) => {
      state.formState.loading = false;
      state.formState.error = null;
      state.formState.success = false;
    },
    resetAddressFormState: (state) => {
      state.addressFormState.loading = false;
      state.addressFormState.error = null;
      state.addressFormState.success = false;
    },
    resetNoteFormState: (state) => {
      state.noteFormState.loading = false;
      state.noteFormState.error = null;
      state.noteFormState.success = false;
    },
    clearCurrentCustomer: (state) => {
      state.currentCustomer = null;
      state.customerError = null;
      state.addresses = [];
      state.notes = [];
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  // Fetch customers
  fetchCustomersRequest,
  fetchCustomersSuccess,
  fetchCustomersFailure,

  // Search customers
  searchCustomersRequest,
  searchCustomersSuccess,
  searchCustomersFailure,

  // Single customer
  fetchCustomerRequest,
  fetchCustomerSuccess,
  fetchCustomerFailure,

  // CRUD
  createCustomerRequest,
  createCustomerSuccess,
  createCustomerFailure,
  updateCustomerRequest,
  updateCustomerSuccess,
  updateCustomerFailure,
  deleteCustomerRequest,
  deleteCustomerSuccess,
  deleteCustomerFailure,

  // Addresses
  fetchCustomerAddressesRequest,
  fetchCustomerAddressesSuccess,
  fetchCustomerAddressesFailure,
  createCustomerAddressRequest,
  createCustomerAddressSuccess,
  createCustomerAddressFailure,
  deleteCustomerAddressRequest,
  deleteCustomerAddressSuccess,
  deleteCustomerAddressFailure,

  // Notes
  fetchCustomerNotesRequest,
  fetchCustomerNotesSuccess,
  fetchCustomerNotesFailure,
  createCustomerNoteRequest,
  createCustomerNoteSuccess,
  createCustomerNoteFailure,
  deleteCustomerNoteRequest,
  deleteCustomerNoteSuccess,
  deleteCustomerNoteFailure,

  // Dashboard
  fetchCustomerDashboardRequest,
  fetchCustomerDashboardSuccess,
  fetchCustomerDashboardFailure,

  // Import/Export
  importCustomersRequest,
  importCustomersSuccess,
  importCustomersFailure,
  exportCustomersRequest,
  exportCustomersSuccess,
  exportCustomersFailure,

  // Reset/Clear
  resetFormState,
  resetAddressFormState,
  resetNoteFormState,
  clearCurrentCustomer,
  clearSearchResults,
} = customersSlice.actions;

export default customersSlice.reducer;
